import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TouchEvent } from "react";
import Container from "./Container";
import { Subheading } from "./text";
import NoiseSphere from "./NoiseSphere";
import DemoCard, {
  DemoChecklistCard,
  DocSnippetCard,
  SummaryCard,
  AccuracyCard,
  ModelSupportCard,
} from "./DemoCard";
import heroAssets from "../data/heroAssets";

type SectionProps = {
  className?: string;
};

const ORANGE_HEX = "#FF8000"; // u_red=1.0, u_green=0.5, u_blue=0.0
// Hero-style docs animation helpers (matched to HeroAnimation)
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
type SlowEvent = { start: number; duration: number; highlight: string; summaryId?: string };
const DOCS_MS = 8000;
const SLOW_FACTOR = 0.01;
const clampMs = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const sumOverlap = (t: number, evs: SlowEvent[]) =>
  evs.reduce((acc, ev) => {
    const s = clampMs(t, 0, DOCS_MS);
    const e = Math.min(s, DOCS_MS);
    const o = Math.max(0, Math.min(ev.start + ev.duration, e) - Math.max(ev.start, 0));
    return acc + o;
  }, 0);
const totalSlowWindow = (evs: SlowEvent[]) =>
  evs.reduce((acc, ev) => acc + Math.max(0, Math.min(ev.duration, Math.max(0, Math.min(DOCS_MS, ev.start + ev.duration) - Math.max(0, ev.start)))), 0);
const docScrollFracAt = (t: number, evs: SlowEvent[]) => {
  const td = clampMs(t, 0, DOCS_MS);
  const coveredSlow = sumOverlap(td, evs);
  const totalSlow = totalSlowWindow(evs);
  const wFull = (DOCS_MS - totalSlow) * 1 + totalSlow * SLOW_FACTOR;
  const wNow = (td - coveredSlow) * 1 + coveredSlow * SLOW_FACTOR;
  return clamp01(wNow / Math.max(1, wFull));
};
const activeHighlightNow = (t: number, evs: SlowEvent[]) => {
  const ev = evs.find((e) => t >= e.start && t < e.start + e.duration);
  return ev ? [ev.highlight] : [];
};

type Item = {
  key: string;
  title: string;
  description: string;
  side: "left" | "right";
};

const items: Item[] = [
  {
    key: "longitudinal",
    title: "Longitudinal chart analysis",
    description:
      "State-of-the-art longitudinal chart ingestion with LLM workflows purpose-built for risk adjustment.",
    side: "left",
  },
  {
    key: "rationale",
    title: "Rationale & evidence packet behind every signal",
    description: "Clear explanation and jump links for every code found.",
    side: "left",
  },
  {
    key: "v28v24",
    title: "V28 native & V24 supported",
    description:
      "Dual support for CMS-HCC V24 & V28 models. Automatic RAF calculations for blended & V28-only PY.",
    side: "left",
  },
  {
    key: "accuracy",
    title: "Best-in-class coding accuracy",
    description:
      "Leapfrog towards 99% final coding accuracy with VitalStrata.",
    side: "right",
  },
  {
    key: "prospective",
    title: "Uncover hidden prospective opportunities",
    description:
      "Suspect complex conditions buried in longitudinal patient charts, frequently missed by humans & NLP tools.",
    side: "right",
  },
  {
    key: "radv",
    title: "Ironclad RADV audit defensibility",
    description: "Protect RAF integrity against RADV ramp-up.",
    side: "right",
  },
];

const BestInClass = ({ className }: SectionProps) => {
  const [selected, setSelected] = useState<string>("longitudinal");
  const [progress, setProgress] = useState<number>(0); // 0..1 countdown progress
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const [prevSelected, setPrevSelected] = useState<string | null>(null);
  const [anim, setAnim] = useState<boolean>(false);
  const lastSelectedRef = useRef<string>(selected);
  const [activeSummaryIds, setActiveSummaryIds] = useState<string[]>([]);
  // Local docs animation loop (8s, same as Hero docs phase)
  const [docTms, setDocTms] = useState<number>(0);
  const docStartRef = useRef<number>(0);
  const docRafRef = useRef<number | null>(null);
  // Accent border style similar to DemoCard's weak border
  const ACCENT_WEAK = "rgba(255,128,0,0.2)";
  // Medium layout carousel geometry and animation (5-card stack)
  const CARD_H = 200;
  const GAP_PX = 24; // matches gap-6
  const STEP_PX = CARD_H + GAP_PX;
  const VIEW_H = 400 + 2 * GAP_PX; // half(prev) + gaps + current + gaps + half(next)
  const BASE_Y = (VIEW_H / 2 - CARD_H / 2) - (2 * STEP_PX); // center current (3rd of 5)
  const SLIDE_MS = 320;
  const [isSliding, setIsSliding] = useState(false);
  const [slideDir, setSlideDir] = useState<"next" | "prev" | null>(null);
  const [animShift, setAnimShift] = useState(0); // px offset from BASE_Y during animation
  // Small layout horizontal geometry (5-card row)
  const CARD_W = 240;
  const STEP_X = CARD_W + GAP_PX;
  const VIEW_W = CARD_W + CARD_W + 2 * GAP_PX; // base formula; overridden by viewport width on small
  // Track viewport width for small layout centering
  const [smViewW, setSmViewW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : VIEW_W
  );
  useEffect(() => {
    const onResize = () => {
      if (typeof window !== "undefined") setSmViewW(window.innerWidth);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    docStartRef.current = performance.now();
    const loop = (now: number) => {
      const t = ((now - docStartRef.current) % DOCS_MS);
      setDocTms(t);
      docRafRef.current = requestAnimationFrame(loop);
    };
    docRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (docRafRef.current) cancelAnimationFrame(docRafRef.current);
    };
  }, []);

  const leftItems = useMemo(
    () => items.filter((i) => i.side === "left"),
    []
  );
  const rightItems = useMemo(
    () => items.filter((i) => i.side === "right"),
    []
  );

  // Auto-advance selection every 5 seconds with animated progress bar
  const order = useMemo(() => [...leftItems, ...rightItems], [leftItems, rightItems]);
  useEffect(() => {
    const durationMs = 5000;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const p = Math.min(1, elapsed / durationMs);
      setProgress(p);
      if (p >= 1 && !isSliding) {
        const idx = order.findIndex((i) => i.key === selected);
        const nextIdx = idx < 0 ? 0 : (idx + 1) % order.length;
        slideTo("next", () => {
        setSelected(order[nextIdx].key);
        startRef.current = performance.now();
        setProgress(0);
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [selected, order, isSliding]);

  // Track previous selection to animate asset swoosh
  useEffect(() => {
    if (lastSelectedRef.current !== selected) {
      setPrevSelected(lastSelectedRef.current);
      setAnim(true);
      const t = setTimeout(() => {
        setAnim(false);
        setPrevSelected(null);
      }, 500);
      lastSelectedRef.current = selected;
      return () => clearTimeout(t);
    }
  }, [selected]);
  const renderAssetFor = (key: string) => {
    switch (key) {
      case "longitudinal":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="block">
              {(() => {
                // Use member A doc and the exact highlight schedule from Hero
                const docA = heroAssets.docSnippets.find((d) => d.memberId === "A") || heroAssets.docSnippets[0];
                const eventsA: SlowEvent[] = [
                  { start: 485, duration: 900, highlight: "orthopnea 2 pillows", summaryId: "sum-A-01-s3" },
                  { start: 1780, duration: 900, highlight: "A1c [8.7% (10/24)]", summaryId: "sum-A-01-s1" },
                  { start: 3335, duration: 900, highlight: "UACR [280 mg/g (11/24)]", summaryId: "sum-A-01-s2" },
                  { start: 5000, duration: 900, highlight: "eGFR [42]", summaryId: "sum-A-01-s2" },
                  { start: 6840, duration: 900, highlight: "torsemide [20 mg qd]", summaryId: "sum-A-01-s3" },
                ];
                const scrollPct = docScrollFracAt(docTms, eventsA);
                const activeHighlights = activeHighlightNow(docTms, eventsA);
                return (
                  <DocSnippetCard
                    title={docA?.title}
                    subtitle={"CA · SSA 06065"}
                    text={docA?.text}
                    activeHighlights={activeHighlights}
                    scrollPct={scrollPct}
                    pulse={true}
                    scrollRangePct={720}
                    accent={true}
                    accentColor={ORANGE_HEX}
                  />
                );
              })()}
            </div>
          </div>
        );
      case "rationale":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="block">
              {(() => {
                const demoA =
                  heroAssets.demoSignals.find(
                    (d) => (d as any).id === "demo-A" || d.kind === "add"
                  ) || heroAssets.demoSignals[0];
                return (
                  <DemoCard
                    title={(demoA as any)?.title}
                    code={(demoA as any)?.code}
                    badgeLabel={(demoA as any)?.badgeLabel}
                    badgeIcon={(demoA as any)?.badgeIcon}
                    showActions={(demoA as any)?.showActions}
                    primaryActionLabel={(demoA as any)?.primaryActionLabel}
                    secondaryActionLabel={(demoA as any)?.secondaryActionLabel}
                    penaltyText={(demoA as any)?.penaltyText}
                    bottomTagText={(demoA as any)?.bottomTagText}
                    meatValidatedLetters={(demoA as any)?.meatValidatedLetters}
                    secondarySectionTitle={(demoA as any)?.secondarySectionTitle}
                    secondarySectionText={(demoA as any)?.secondarySectionText}
                    rationaleList={(demoA as any)?.rationale}
                    accent={true}
                    accentColor={ORANGE_HEX}
                  />
                );
              })()}
            </div>
          </div>
        );
      case "v28v24":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="block">
              <ModelSupportCard accent={true} accentColor={ORANGE_HEX} />
            </div>
          </div>
        );
      case "accuracy":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="block">
              <AccuracyCard accent={true} accentColor={ORANGE_HEX} />
            </div>
          </div>
        );
      case "prospective":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="block">
              {(() => {
                const demoB1 =
                  heroAssets.demoSignals.find(
                    (d) => d.memberId === "B" && d.kind === "suspect"
                  ) || heroAssets.demoSignals[0];
                return (
                  <DemoCard
                    title={(demoB1 as any)?.title}
                    code={(demoB1 as any)?.code}
                    badgeLabel={(demoB1 as any)?.badgeLabel}
                    showActions={(demoB1 as any)?.showActions}
                    bottomTagText={(demoB1 as any)?.bottomTagText}
                    secondarySectionTitle={(demoB1 as any)?.secondarySectionTitle}
                    secondarySectionText={(demoB1 as any)?.secondarySectionText}
                    meatValidatedLetters={(demoB1 as any)?.meatValidatedLetters}
                    penaltyText={(demoB1 as any)?.penaltyText}
                    rationaleList={(demoB1 as any)?.rationale}
                    badgeIcon={(demoB1 as any)?.badgeIcon}
                    accent={true}
                    accentColor={ORANGE_HEX}
                  />
                );
              })()}
            </div>
          </div>
        );
      case "radv":
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-6">
              {(() => {
                // Use the "add" demo signal and corresponding summary (member A if available)
                const demoAdd =
                  heroAssets.demoSignals.find(
                    (d) => (d as any).id === "demo-A" || d.kind === "add"
                  ) || heroAssets.demoSignals[0];
                const summary =
                  heroAssets.summaries.find((s) => s.memberId === "A") ||
                  heroAssets.summaries[0];
                return (
                  <>
                    <DemoCard
                      title={(demoAdd as any)?.title}
                      code={(demoAdd as any)?.code}
                      badgeLabel={(demoAdd as any)?.badgeLabel}
                      badgeIcon={(demoAdd as any)?.badgeIcon}
                      showActions={(demoAdd as any)?.showActions}
                      primaryActionLabel={(demoAdd as any)?.primaryActionLabel}
                      secondaryActionLabel={(demoAdd as any)?.secondaryActionLabel}
                      penaltyText={(demoAdd as any)?.penaltyText}
                      bottomTagText={(demoAdd as any)?.bottomTagText}
                      meatValidatedLetters={(demoAdd as any)?.meatValidatedLetters}
                      secondarySectionTitle={(demoAdd as any)?.secondarySectionTitle}
                      secondarySectionText={(demoAdd as any)?.secondarySectionText}
                      rationaleList={(demoAdd as any)?.rationale}
                      onRationaleHover={(id) => {
                        if (!id) {
                          setActiveSummaryIds([]);
                          return;
                        }
                        const refs =
                          (demoAdd as any)?.rationale?.find((r: any) => r.id === id)
                            ?.summaryRefs || [];
                        setActiveSummaryIds(refs);
                      }}
                      accent={true}
                      accentColor={ORANGE_HEX}
                    />
                    <SummaryCard
                      title={summary?.title}
                      sentenceList={summary?.sentences}
                      disableHighlights={true}
                      activeSentenceIds={activeSummaryIds}
                      accent={true}
                      accentColor={ORANGE_HEX}
                    />
                  </>
                );
              })()}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderList = (list: Item[]) => (
    <div className="space-y-14">
      {list.map((i) => {
        const isSelected = i.key === selected;
        return (
          <button
            key={i.key}
            onClick={() => {
              setSelected(i.key);
              setProgress(0);
              startRef.current = performance.now();
            }}
            className="group text-left cursor-pointer"
            aria-selected={isSelected}
          >
            {isSelected ? (
              <div className="mb-3 h-px w-full bg-white/20">
                <div
                  className="h-px bg-[#FF8000] transition-none"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            ) : (
              <div className="mb-3 h-px w-full bg-transparent" />
            )}
            <div
              className="text-2xl font-light text-white group-hover:text-[#FF8000]"
              style={isSelected ? { color: ORANGE_HEX } : undefined}
            >
              {i.title}
            </div>
            <div 
              className="mt-2 text-base font-light text-white/80 group-hover:text-[#FF8000] max-w-xs"
              style={isSelected ? { color: ORANGE_HEX } : undefined}
            >
              {i.description}
            </div>
          </button>
        );
      })}
    </div>
  );

  // Helpers for navigation and ordering (used across layouts)
  const goToIndex = (idx: number) => {
    const clamped = ((idx % order.length) + order.length) % order.length;
    setSelected(order[clamped].key);
    setProgress(0);
    startRef.current = performance.now();
  };
  const selectedIdx = order.findIndex((i) => i.key === selected);
  const slideTo = (dir: "next" | "prev", onAfter?: () => void) => {
    if (isSliding) return;
    setSlideDir(dir);
    setIsSliding(true);
    setAnimShift(dir === "next" ? -STEP_PX : STEP_PX);
    window.setTimeout(() => {
      setIsSliding(false);
      setSlideDir(null);
      setAnimShift(0);
      onAfter?.();
    }, SLIDE_MS);
  };
  const next = () => {
    const idx = selectedIdx < 0 ? 0 : selectedIdx;
    slideTo("next", () => goToIndex(idx + 1));
  };
  const prev = () => {
    const idx = selectedIdx < 0 ? 0 : selectedIdx;
    slideTo("prev", () => goToIndex(idx - 1));
  };

  // Small layout swipe handlers
  const touchStartXRef = useRef<number | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartXRef.current == null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartXRef.current;
    const delta = endX - touchStartXRef.current;
    const THRESHOLD = 40;
    if (delta > THRESHOLD) {
      prev();
    } else if (delta < -THRESHOLD) {
      next();
    }
    touchStartXRef.current = null;
  };

  return (
    <section id="raf-precision-ai" className={clsx("py-24", className)}>
      <Container>
        <div className="text-lg text-center md:text-left md:text-sm font-base text-gray-400">INTRODUCING RAF PRECISION AI</div>
        {/* Large layout (>= lg): unchanged */}
        <div className="relative mt-12 hidden lg:block">
          {/* Graph paper layer spanning under the text columns */}
          <div className="absolute inset-x-0 top-0 mx-48 md:mx-96 z-0">
            <div className="relative h-[240px] md:h-[480px]">
              <div
                className="absolute inset-0 rounded-sm pointer-events-none"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                  backgroundPosition: "0 0",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(ellipse at center, rgba(12,16,28,0) 62%, rgba(12,16,28,1) 92%), linear-gradient(to right, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 12%, rgba(12,16,28,0) 88%, rgba(12,16,28,1) 100%), linear-gradient(to bottom, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 12%, rgba(12,16,28,0) 88%, rgba(12,16,28,1) 100%)",
                  backgroundBlendMode: "normal",
                }}
              />
            </div>
          </div>
          {/* Foreground content over graph */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-y-20 md:gap-x-24">
            <div className="z-10">{renderList(leftItems)}</div>
            {/* Center column: orb + assets above lists */}
            <div className="relative z-20 min-h-[240px] md:min-h-[480px] flex items-center justify-center">
              <div className="relative">
                <div className="relative h-[240px] w-[240px] sm:h-[280px] sm:w-[280px]">
                  <NoiseSphere version={0} demo_is_active={false} />
                </div>
                {/* Assets layered over the orb */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <style>
                    {`
                      @keyframes swooshIn {
                        0% { transform: scale(1); opacity: 1; filter: blur(0px); }
                        100% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                      }
                      @keyframes swooshOut {
                        0% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                        100% { transform: scale(1); opacity: 1; filter: blur(0px); }
                      }
                    `}
                  </style>
                  {prevSelected && anim && (
                    <div style={{ animation: "swooshIn 500ms ease forwards" }}>
                      {renderAssetFor(prevSelected)}
                    </div>
                  )}
                  <div style={{ animation: anim ? "swooshOut 500ms ease forwards" : undefined }}>
                    {renderAssetFor(selected)}
                  </div>
                </div>
              </div>
            </div>
            <div className="z-10">{renderList(rightItems)}</div>
          </div>
        </div>

        {/* Medium layout (md to <lg): left vertical carousel; right orb+asset */}
        <div className="relative mt-10 hidden md:block lg:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-16 gap-y-10 items-center">
            {/* Left column: vertical carousel */}
            <div className="relative flex-1">
              {/* Viewport that clips prev/next at their midpoints; renders 5 cards for smooth slide */}
              <div className="relative overflow-hidden" style={{ height: `${VIEW_H}px` }}>
                {/* Area-level fades (top/bottom) */}
                <div className="pointer-events-none absolute inset-0 z-10">
                  <div
                    className="absolute inset-x-0 top-0"
                    style={{
                      height: "96px",
                      backgroundImage:
                        "linear-gradient(to bottom, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                    }}
                  />
                  <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: "96px",
                      backgroundImage:
                        "linear-gradient(to top, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                    }}
                  />
                </div>
                {(() => {
                  const n = order.length || 1;
                  const i0 = ((selectedIdx - 2) % n + n) % n;
                  const i1 = ((selectedIdx - 1) % n + n) % n;
                  const i2 = selectedIdx >= 0 ? selectedIdx % n : 0;
                  const i3 = (i2 + 1) % n;
                  const i4 = (i2 + 2) % n;
                  const seq = [i0, i1, i2, i3, i4];
                  const stackStyle: React.CSSProperties = {
                    transform: `translateY(${BASE_Y + animShift}px)`,
                    transition: isSliding ? `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : undefined,
                    willChange: "transform",
                  };
                  return (
                    <div className="flex flex-col items-stretch gap-6" style={stackStyle}>
                      {seq.map((idxInSeq, k) => {
                        const it = order[idxInSeq];
                        const isCurrent = k === 2;
                        return (
                          <div key={`mid-${idxInSeq}-${k}`} className="w-full">
                            {isCurrent ? (
                              <div className="relative h-[200px]">
                                {/* Floating progress bar (does not affect layout) */}
                                <div className="absolute top-0 left-0 right-0 h-px bg-white/20 z-20 pointer-events-none">
                                  <div
                                    className="h-px bg-[#FF8000] transition-none"
                                    style={{ width: `${progress * 100}%` }}
                                  />
                                </div>
                                <div className="rounded-none border p-5 h-[200px] overflow-hidden" style={{ borderColor: ACCENT_WEAK }}>
                                  <div className="h-full flex flex-col items-start justify-center text-left">
                                    <div className="text-2xl font-light" style={{ color: ORANGE_HEX }}>
                                      {it?.title}
                                    </div>
                                    <div className="mt-2 text-base font-light max-w-md" style={{ color: ORANGE_HEX }}>
                                      {it?.description}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  // neighbor click triggers animated slide
                                  if (k === 1) prev();
                                  else if (k === 3) next();
                                }}
                                className="block text-left w-full"
                              >
                                <div className="rounded-none border border-white/20 p-5 h-[200px] overflow-hidden">
                                  <div className="h-full flex flex-col items-start justify-center text-left">
                                    <div className="text-2xl font-light text-white">
                                      {it?.title}
                                    </div>
                                    <div className="mt-2 text-base font-light text-white/80 max-w-md">
                                      {it?.description}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
            {/* Right column: orb + asset */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="relative h-[220px] w-[220px] sm:h-[260px] sm:w-[260px]">
                  <NoiseSphere version={0} demo_is_active={false} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <style>
                    {`
                      @keyframes swooshIn {
                        0% { transform: scale(1); opacity: 1; filter: blur(0px); }
                        100% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                      }
                      @keyframes swooshOut {
                        0% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                        100% { transform: scale(1); opacity: 1; filter: blur(0px); }
                      }
                    `}
                  </style>
                  {prevSelected && anim && (
                    <div style={{ animation: "swooshIn 500ms ease forwards" }}>
                      {renderAssetFor(prevSelected)}
                    </div>
                  )}
                  <div style={{ animation: anim ? "swooshOut 500ms ease forwards" : undefined }}>
                    {renderAssetFor(selected)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small layout (<md): orb + animation above; horizontal carousel below; swipe enabled */}
        <div className="relative mt-10 md:hidden -mx-6">
          {/* Orb + asset above */}
          <div className="mt-20 mb-20 flex items-center justify-center">
            <div className="relative">
              <div className="relative h-[200px] w-[200px] sm:h-[240px] sm:w-[240px]">
                <NoiseSphere version={0} demo_is_active={false} />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <style>
                  {`
                    @keyframes swooshIn {
                      0% { transform: scale(1); opacity: 1; filter: blur(0px); }
                      100% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                    }
                    @keyframes swooshOut {
                      0% { transform: scale(0.1); opacity: 0; filter: blur(2px); }
                      100% { transform: scale(1); opacity: 1; filter: blur(0px); }
                    }
                  `}
                </style>
                {prevSelected && anim && (
                  <div style={{ animation: "swooshIn 500ms ease forwards" }}>
                    {renderAssetFor(prevSelected)}
                  </div>
                )}
                <div style={{ animation: anim ? "swooshOut 500ms ease forwards" : undefined }}>
                  {renderAssetFor(selected)}
                </div>
              </div>
            </div>
          </div>
          {/* Horizontal carousel below orb */}
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 w-screen">
              {/* Gradient fades (left/right) */}
              <div className="pointer-events-none absolute inset-0 z-10">
                <div
                  className="absolute left-0 top-0 bottom-0 w-24"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                  }}
                />
                <div
                  className="absolute right-0 top-0 bottom-0 w-24"
                  style={{
                    backgroundImage:
                      "linear-gradient(to left, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                  }}
                />
              </div>
              {(() => {
              const n = order.length || 1;
              const i0 = ((selectedIdx - 2) % n + n) % n;
              const i1 = ((selectedIdx - 1) % n + n) % n;
              const i2 = selectedIdx >= 0 ? selectedIdx % n : 0;
              const i3 = (i2 + 1) % n;
              const i4 = (i2 + 2) % n;
              const seq = [i0, i1, i2, i3, i4];
               // Span full viewport width and center current card
               const stackStyle: React.CSSProperties = { width: "100vw" };
              const rowStyle: React.CSSProperties = {
                 transform: `translateX(${(smViewW / 2 - CARD_W / 2) - (2 * STEP_X) + (animShift * (STEP_X / STEP_PX))}px)`,
                transition: isSliding ? `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : undefined,
                willChange: "transform",
              };
              return (
                 <div className="mx-auto overflow-hidden" style={stackStyle}>
                  <div
                    className="flex items-stretch gap-6"
                    style={rowStyle}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                  >
                    {seq.map((idxInSeq, k) => {
                      const it = order[idxInSeq];
                      const isCurrent = k === 2;
                      return (
                        <div key={`sm-${idxInSeq}-${k}`} className="shrink-0">
                          {isCurrent ? (
                            <div className="relative w-[240px] h-[240px]">
                              {/* Floating progress bar (does not affect layout) */}
                              <div className="absolute top-0 left-0 right-0 h-px bg-white/20 z-20 pointer-events-none">
                                <div
                                  className="h-px bg-[#FF8000] transition-none"
                                  style={{ width: `${progress * 100}%` }}
                                />
                              </div>
                              <div className="rounded-none border p-5 w-[240px] h-[240px] overflow-hidden" style={{ borderColor: ACCENT_WEAK }}>
                                <div className="text-2xl font-light" style={{ color: ORANGE_HEX }}>
                                  {it?.title}
                                </div>
                                <div className="mt-2 text-base font-light" style={{ color: ORANGE_HEX }}>
                                  {it?.description}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                if (k === 1) prev();
                                else if (k === 3) next();
                              }}
                              className="text-left"
                            >
                              <div className="rounded-none border border-white/20 p-5 w-[240px] h-[240px] overflow-hidden">
                                <div className="text-2xl font-light text-white">
                                  {it?.title}
                                </div>
                                <div className="mt-2 text-base font-light text-white/80">
                                  {it?.description}
                                </div>
                              </div>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            </div>
            {/* Reserve vertical space for the absolute carousel */}
            <div className="h-[240px]" />
          </div>
        </div>
      </Container>
    </section>
  );
};

export default BestInClass;


